import { dateFormatter2 as dateFormatter, DateFormatTypes } from '@jfs/utils'
import { flow, types } from 'mobx-state-tree'
import { actionMenuStrings } from '../../data/static/strings'
import { getInsightsRecord } from '../../network/getInsightsRecord'
import { getInsightsSummary } from '../../network/getInsightsSummary'
import { lineColors } from '../../screens/Insights/graph/chartConfig'
import { customToDateTime, fromDateTime } from '../../util/util'
import { customError } from '../static/strings'
import { RootStore } from '../store'

const Summary = types.model('Summary', {
  key: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  formatedVal: types.optional(types.string, ''),
  marginPercent: types.optional(types.string, ''),
  margin: types.optional(types.number, 1),
  duration: types.optional(types.string, ''),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.optional(types.string, ''),
})

const XAxisModel = types.model('xAxisModel', {
  text: types.string,
  isChecked: types.boolean,
})

const GraphPrefrencesModel = types
  .model('GraphPrefrencesModel', {
    transactions: types.optional(types.string, 'Value'),
    segmentedBy: types.optional(types.string, 'Payment Method'),
    segmentedVs: types.optional(types.string, ''),
    durationType: types.optional(types.string, 'Hourly'),
    selectedTab: types.optional(types.number, 0),
    maxValY: types.optional(types.number, 100),
    showByPercentage: types.optional(types.boolean, false),
    xAxis: types.array(XAxisModel),
    selectedDate: DateRange,
    dateValue: types.optional(types.string, actionMenuStrings.today),
  })
  .actions((self) => ({
    setDateValue(data: string) {
      const own = self
      own.dateValue = data
    },
    updateDates(dateObj: any): void {
      const own = self
      own.selectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.selectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.selectedDate.position.top = dateObj?.position?.top ?? 0
      own.selectedDate.position.left = dateObj?.position?.left ?? 0
      own.selectedDate.position.visible = dateObj?.position?.visible ?? false
      own.selectedDate.dateLabel = dateObj?.dateLabel
    },
  }))

const Segments = types.model('Segments', {
  segment: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  percentage: types.optional(types.string, ''),
  checked: types.optional(types.boolean, true),
})

const summaryData = [
  {
    key: 'Total Count',
    value: '',
    marginPercent: '',
    margin: 0,
    duration: '30 days',
  },
  {
    key: 'Total Value',
    value: '',
    marginPercent: '',
    margin: 0,
    duration: '30 days',
  },
  {
    key: 'Overall Success Rate',
    value: '',
    marginPercent: '',
    margin: 0,
    duration: '30 days',
  },
  {
    key: 'Average TIcket Size',
    value: '',
    marginPercent: '',
    margin: 0,
    duration: '30 days',
  },
]
const Insights = types
  .model('Insights', {
    summary: types.array(Summary),
    graphPrefrences: GraphPrefrencesModel,
    graphRecordData: types.array(types.array(types.union(types.string, types.number, types.Date))),
    graphPercentData: types.array(types.array(types.union(types.string, types.number, types.Date))),
    barGraphRecordData: types.array(types.array(types.union(types.string, types.number, types.Date))),
    segmentByData: types.array(Segments),
    triggerRefresh: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    fetchInsightsSummary: flow(function* fetchInsightsSummary(mid) {
      const getType: any = {
        0: 'TRANSACTION',
        // 1: 'SETTLEMENTS', //INFO: Removed as settlements is not there as of now
        1: 'REFUND',
      }
      const tabValue = self.graphPrefrences.selectedTab
      const toDate = self.graphPrefrences.selectedDate.toDate ?? new Date(Date.now())
      const fromDate = self.graphPrefrences.selectedDate.fromDate
      const getReq: any = {
        inputFilter: {
          merchantId: mid,
          responseType: 'DEFAULT',
          startDateTime: dateFormatter(fromDate, DateFormatTypes.YYYYMMDD).trim(),
          endDateTime: dateFormatter(toDate, DateFormatTypes.YYYYMMDD).trim(),
          transactionType: getType[tabValue],
        },
        configParams: {
          mediaType: 'JSON',
        },
        outputDomains: ['totalCount', 'totalVolume', 'successRate', 'averageTickets'],
        //to identify type in mocks
        tempParam: self.graphPrefrences.dateValue,
      }
      let response
      try {
        response = yield getInsightsSummary(RootStore.user.session.getTokens, getReq)
        if (response?.length > 0) {
          self.summary = JSON.parse(JSON.stringify(response))
        } else {
          self.summary = JSON.parse(JSON.stringify(summaryData))
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchInsightsRecord: flow(function* fetchInsightsRecord(reqBody, mid, onlySegmentedBy) {
      if (mid.length === 0) {
        return
      }
      const getType: any = {
        0: 'TRANSACTION',
        // 1: 'SETTLEMENT',//INFO: Removed as settlements is not there as of now
        1: 'REFUND',
      }
      const tabValue = self.graphPrefrences.selectedTab
      const isCustomRange =
        self.graphPrefrences.dateValue === 'Custom Range' ||
        self.graphPrefrences.dateValue === actionMenuStrings.yesterday
      reqBody = {
        ...reqBody,
        segmentedVs: !onlySegmentedBy ? self.graphPrefrences.segmentedVs : undefined,
        type: getType[tabValue],
        mid: mid,
        showPercent: true, //self.graphPrefrences.showByPercentage,
        fromDate: fromDateTime(self.graphPrefrences.selectedDate.fromDate),
        toDate: customToDateTime(self.graphPrefrences.selectedDate.toDate ?? new Date(Date.now()), isCustomRange),
        dateValue: self.graphPrefrences.dateValue,
      }

      let response
      try {
        response = yield getInsightsRecord(RootStore.user.session.getTokens, reqBody)
        self.graphPrefrences.maxValY = self.graphPrefrences.showByPercentage ? 100 : response?.maxVal
        self.graphRecordData = response?.data
        if (self.graphPrefrences.segmentedVs?.length === 0 || onlySegmentedBy) {
          self.graphRecordData = response.data
        } else {
          self.barGraphRecordData = response?.data
        }
        if (self.graphPrefrences.segmentedVs?.length > 0 && !onlySegmentedBy) {
          self.graphPrefrences.xAxis = response?.xAxis
        }
        self.segmentByData = response?.segmentByData
        self.graphPercentData = response?.percentData
      } catch (e) {
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updateTransactionType: (transaction: any) => {
      self.graphPrefrences.transactions = transaction
    },
    updateSegmentedBy: (segmentedByData: any) => {
      self.graphPrefrences.segmentedBy = segmentedByData
    },
    updateSegmentedVs: (segmentedVsData: any) => {
      self.graphPrefrences.segmentedVs = segmentedVsData
    },
    updateDurationType: (durationTypeData: any) => {
      self.graphPrefrences.durationType = durationTypeData
    },
    updateSelectedTab: (selectedTab: number) => {
      self.graphPrefrences.selectedTab = selectedTab
    },
    updateShowByPercentage: (show: boolean) => {
      self.graphPrefrences.showByPercentage = show
    },
    resetGraphPerfrences: () => {
      self.graphPrefrences.transactions = 'By Value'
      self.graphPrefrences.segmentedBy = 'Payment Method'
      self.graphPrefrences.segmentedVs = ''
      self.graphPrefrences.durationType = 'Hourly'
      self.graphPrefrences.showByPercentage = false
    },
    triggerRefreshApi: () => {
      self.triggerRefresh = !self.triggerRefresh
    },
    getGraphPrefrences: () => {
      return self.graphPrefrences
    },
    setSegmentedByData: (data: any) => {
      self.segmentByData = data
    },
    updateXaxisData: (text: any, isChecked: boolean) => {
      const retData: any = []
      self.graphPrefrences.xAxis.forEach((item) => {
        if (item.text !== text) {
          retData.push({ ...item })
        } else {
          const newItem = {
            ...item,
            isChecked: isChecked,
          }
          retData.push(newItem)
        }
      })
      self.graphPrefrences.xAxis = retData
    },
    updateSegmentedByData: (segment: any, isChecked: boolean) => {
      const retData: any = []
      self.segmentByData.map((item) => {
        if (item.segment !== segment) {
          retData.push(item)
        } else {
          const newItem = {
            ...item,
            checked: isChecked,
          }
          retData.push(newItem)
        }
      })
      self.segmentByData = retData
    },
  }))
  .views((self) => ({
    getSummary() {
      const retData: any = []
      self?.summary.map((item) => {
        const summaryItem = {
          title: item?.key,
          subtitle: item?.value,
          formatedVal: item?.formatedVal,
          percentage: item?.marginPercent,
          margin: item?.margin,
          duration: item?.duration,
        }
        retData.push(summaryItem)
      })
      return retData
    },
    getGraphPrefrences() {
      return self.graphPrefrences
    },
    getSelectedTab() {
      return self.graphPrefrences.selectedTab
    },
    getGraphData: () => {
      const data: any[] = []
      const dataToShow = self.graphPrefrences.showByPercentage
        ? self?.graphPercentData
        : self.graphPrefrences?.segmentedVs?.length > 0
        ? self.barGraphRecordData
        : self?.graphRecordData
      dataToShow.map((item) => {
        const rowArray: any[] = []
        item.map((rowField: any) => {
          if (rowField === 'customTooltipFieldObj') {
            rowArray.push({ role: 'tooltip', p: { html: true } })
          } else {
            rowArray.push(rowField)
          }
        })
        data.push(rowArray)
      })
      return data
    },
    getXaxisCheckBoxData: () => {
      return self.graphPrefrences.segmentedVs.length > 0 ? self.graphPrefrences.xAxis : []
    },
    getSegmentedByData: () => {
      const retData: any[] = []
      self.segmentByData.forEach((item: any, index: any) => {
        retData.push({ ...item, color: lineColors[index % 15] })
      })
      return retData
    },
    getTotal: () => {
      let total = 0
      self.segmentByData.map((item: any) => {
        total += Number(item.value.replace('₹', ''))
      })
      return total
    },
    hasRowHundredPercent: (rowArray) => {
      const totalPercentage = rowArray.reduce((total, rowItem, index) => {
        let percentage = 0
        if (index !== 0 && typeof rowItem === 'number') {
          percentage = rowItem
        }
        return total + percentage
      }, 0)

      return totalPercentage === 100
    },
    isValidPercentageData: () => {
      for (let i = 1; i < self?.graphPercentData.length; i++) {
        if (!self?.hasRowHundredPercent(self.graphPercentData[i])) {
          return false
        }
      }
      return true
    },
    getXaxisTicks: (chartData: any) => {
      const retData: any = []
      if (self.graphPrefrences.durationType !== 'Hourly' || chartData.length <= 25) {
        chartData?.map((item: any, index: number) => {
          if (index > 0) {
            retData.push(item[0])
          }
        })
        return retData
      }
      return undefined
    },
  }))

const InsightsData = Insights.create({
  summary: summaryData,
  graphPrefrences: GraphPrefrencesModel.create({
    selectedDate: {
      fromDate: new Date().setHours(0, 0, 0, 0),
      toDate: new Date(Date.now()),
      position: {
        top: 0,
        left: 0,
        visible: false,
      },
    },
  }),
  graphRecordData: [],
  segmentByData: [],
})
export { Insights, InsightsData }
